import React, { useState } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Send, Check } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useContact, ContactOptionType } from "@/hooks/use-contact";

const ContactOption = ({
  icon,
  title,
  description,
  email,
  delay,
}: ContactOptionType) => (
  <div
    className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:shadow-md hover:-translate-y-2 animate-fade-in opacity-0 transition-all duration-300"
    style={{ animationDelay: `${delay}s` }}
  >
    <div className="w-12 h-12 rounded-full bg-[#1A2B4D]/10 flex items-center justify-center text-[#1A2B4D] mb-4">
      {icon}
    </div>
    <h3 className="text-xl font-bold text-[#1A2B4D] mb-2">{title}</h3>
    <p className="text-[#1A2B4D]/70 mb-4">{description}</p>
    <a
      href={`mailto:${email}`}
      className="text-[#1A2B4D] font-medium hover:text-gold transition-colors inline-flex items-center"
    >
      {email}
    </a>
  </div>
);

const Contact = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const { toast } = useToast();
  const { contactOptions, sendMessage, isSubmitting, isSubmitted, error } =
    useContact();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const success = await sendMessage(name, email, subject, message);

    if (success) {
      toast({
        title: "Message sent successfully!",
        description: "We'll get back to you as soon as possible.",
        duration: 5000,
      });

      // Reset form after success
      setName("");
      setEmail("");
      setSubject("");
      setMessage("");

      // Reset success state after a delay
      setTimeout(() => {}, 3000);
    } else {
      toast({
        title: "Message could not be sent",
        description: error || "Please try again later.",
        variant: "destructive",
        duration: 5000,
      });
    }
  };

  return (
    <>
      <Navbar />

      <main className="pt-20">
        {/* Hero section */}
        <section className="relative py-16 md:py-24 bg-[#1A2B4D] overflow-hidden">
          <div className="absolute top-0 left-0 w-64 h-64 bg-gold/10 rounded-full -translate-x-1/2 -translate-y-1/2 blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-80 h-80 bg-gold/10 rounded-full translate-x-1/2 translate-y-1/2 blur-3xl"></div>

          <div className="container mx-auto px-4 md:px-6 relative">
            <div className="max-w-3xl mx-auto text-center">
              <span
                className="inline-block px-3 py-1 bg-white/10 text-white text-sm rounded-full mb-4 animate-slide-in-up opacity-0"
                style={{ animationDelay: "0.1s" }}
              >
                Contact Us
              </span>
              <h1
                className="text-4xl md:text-5xl font-bold text-white mb-6 animate-slide-in-up opacity-0"
                style={{ animationDelay: "0.2s" }}
              >
                We'd Love to Hear From You
              </h1>
              <p
                className="text-xl text-white/90 animate-slide-in-up opacity-0"
                style={{ animationDelay: "0.3s" }}
              >
                Whether you have a question, feedback, or business inquiry, our
                team is here to help.
              </p>
            </div>
          </div>
        </section>

        {/* Contact options */}
        <section className="py-16 md:py-24">
          <div className="container mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
              {contactOptions.map((option, index) => (
                <ContactOption key={index} {...option} />
              ))}
            </div>

            {/* Contact form */}
            <div className="max-w-2xl mx-auto">
              <div
                className="text-center mb-10 animate-fade-in opacity-0"
                style={{ animationDelay: "0.4s" }}
              >
                <h2 className="text-3xl font-bold text-[#1A2B4D] mb-4">
                  Send Us a Message
                </h2>
                <p className="text-[#1A2B4D]/70">
                  Fill out the form below and we'll get back to you as soon as
                  possible.
                </p>
              </div>

              {error && (
                <div className="bg-red-50 text-red-700 p-4 rounded-md mb-6 animate-pulse">
                  {error}
                </div>
              )}

              <form
                onSubmit={handleSubmit}
                className="space-y-6 animate-fade-in opacity-0"
                style={{ animationDelay: "0.5s" }}
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div
                    className="space-y-2 animate-fade-in-left opacity-0"
                    style={{ animationDelay: "0.6s" }}
                  >
                    <label
                      htmlFor="name"
                      className="text-sm font-medium text-[#1A2B4D]"
                    >
                      Your Name
                    </label>
                    <Input
                      id="name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Emily"
                      className="border-gray-200 focus:border-[#1A2B4D] transition-all duration-300"
                      required
                      disabled={isSubmitting || isSubmitted}
                    />
                  </div>
                  <div
                    className="space-y-2 animate-fade-in-right opacity-0"
                    style={{ animationDelay: "0.6s" }}
                  >
                    <label
                      htmlFor="email"
                      className="text-sm font-medium text-[#1A2B4D]"
                    >
                      Email Address
                    </label>
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="emily@example.com"
                      className="border-gray-200 focus:border-[#1A2B4D] transition-all duration-300"
                      required
                      disabled={isSubmitting || isSubmitted}
                    />
                  </div>
                </div>

                <div
                  className="space-y-2 animate-fade-in opacity-0"
                  style={{ animationDelay: "0.7s" }}
                >
                  <label
                    htmlFor="subject"
                    className="text-sm font-medium text-[#1A2B4D]"
                  >
                    Subject
                  </label>
                  <Input
                    id="subject"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    placeholder="How can we help you?"
                    className="border-gray-200 focus:border-[#1A2B4D] transition-all duration-300"
                    required
                    disabled={isSubmitting || isSubmitted}
                  />
                </div>

                <div
                  className="space-y-2 animate-fade-in opacity-0"
                  style={{ animationDelay: "0.8s" }}
                >
                  <label
                    htmlFor="message"
                    className="text-sm font-medium text-[#1A2B4D]"
                  >
                    Message
                  </label>
                  <Textarea
                    id="message"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Please provide details about your inquiry..."
                    className="border-gray-200 focus:border-[#1A2B4D] min-h-[120px] transition-all duration-300"
                    required
                    disabled={isSubmitting || isSubmitted}
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full bg-[#1A2B4D] hover:bg-[#2E3F66] text-white font-medium transition-all duration-300 h-12 animate-fade-in opacity-0 hover:scale-[1.02]"
                  disabled={isSubmitting || isSubmitted}
                  style={{ animationDelay: "0.9s" }}
                >
                  {isSubmitting ? (
                    <span className="flex items-center">
                      <span className="mr-2">Sending</span>
                      <span className="animate-pulse">...</span>
                    </span>
                  ) : isSubmitted ? (
                    <span className="flex items-center">
                      <Check className="h-4 w-4 mr-2" />
                      <span>Message Sent!</span>
                    </span>
                  ) : (
                    <span className="flex items-center">
                      <Send className="h-4 w-4 mr-2" />
                      <span>Send Message</span>
                    </span>
                  )}
                </Button>
              </form>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
};

export default Contact;
